import HttpRequest from './helpers/httpRequest';

export default function hyperdbTriggers({
  triggerName, namespace, entryPoint, collectionName,
  querySearchListing, quickSearch, config = {}, filters = null,
  successMessageCreate, successMessageUpdate, successMessageDelete,
}) {
  const httpRequest = HttpRequest(config);
  const doDispatch = (dispatch, { type, payload }) => {
    return dispatch({ type: `${namespace}__${type}`, payload });
  };
  const url = (config.API_ROOT_URL || '') + (entryPoint || '');
  const defaultLanguage = config.DEFAULT_LANGUAGE;

  const generateQueryOptions = (query, params) => {
    let queryOptions = { ...query[1] || {} };
    const { searchKeyword, selectedFilter, useAdvancedFilters, advancedFilters, page, sort } = params;
    if (searchKeyword) {
      if (!queryOptions.where) queryOptions.where = { $all: [] };
      queryOptions.where.$all.push(quickSearch(searchKeyword));
    }
    if (filters && selectedFilter) {
      const conditions = filters[selectedFilter];
      if (conditions) {
        if (!queryOptions.where) queryOptions.where = { $all: [] };
        queryOptions.where.$all.push(conditions());
      }
    }
    if (useAdvancedFilters) {
      const conditions = queryFromAdvancedFilters(advancedFilters);
      if (conditions.length > 0) {
        if (!queryOptions.where) queryOptions.where = { $all: [] };
        queryOptions.where.$all = queryOptions.where.$all.concat(conditions);
      }
    }
    if (page) {
      queryOptions.offset = (config.NB_DISPLAYED_OBJECTS || 50) * (page - 1);
    }
    queryOptions.limit = config.NB_DISPLAYED_OBJECTS || 50;
    if (sort) {
      queryOptions.order = { [sort[0]]: sort[1] };
    }
    queryOptions.locale = defaultLanguage;
    if (params.customQueryOptions) queryOptions = { ...queryOptions, ...params.customQueryOptions };
    return queryOptions;
  };

  return {
    fetchListing(dispatch, getState, params = {}) {
      doDispatch(dispatch, { type: 'FETCH_LISTING', payload: params });
      const query = querySearchListing ? querySearchListing : [['*'], { order: { id: 'asc' } }];
      const fields = query[0];
      const queryOptions = generateQueryOptions(query, params);
      return httpRequest('GET', url + `?search=${encodeURI(JSON.stringify([fields, queryOptions]))}`)
        .then(res => {
          doDispatch(dispatch, { type: 'LISTING_FETCHED', payload: res });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    isFetchingListing(dispatch, getState, params) {
      doDispatch(dispatch, { type: 'FETCH_LISTING', payload: params });
    },
    listingFetched(dispatch, getState, payload) {
      doDispatch(dispatch, { type: 'LISTING_FETCHED', payload });
    },
    leaveListing(dispatch) {
      doDispatch(dispatch, { type: 'LEAVE_LISTING' });
    },
    downloadCsv(dispatch, getState) {
      const query = querySearchListing ? querySearchListing : [['*'], { order: { id: 'asc' } }];
      const params = getState(triggerName);
      const { where } = generateQueryOptions(query, params);
      const currentUser = getState('auth').currentUser;
      let downloadUrl = config.FILESTORE_URL + `/${currentUser.client_id}` + entryPoint + '.csv';
      if (where && Object.keys(where).length > 0) downloadUrl += `?conditions=${encodeURI(JSON.stringify(where))}`;
      window.open(downloadUrl, '_blank');
    },
    getObjects(dispatch, getState, { queryOptions, propName, notInStore }) {
      const query = queryOptions ? queryOptions : querySearchListing;
      const fields = query[0];
      const opts = { ...query[1] || {} };
      opts.locale = defaultLanguage;
      opts.order = opts.order || { id: 'asc' };
      return httpRequest('GET', url + `?search=${encodeURI(JSON.stringify([fields, opts]))}`)
        .then(res => {
          if (!notInStore) {
            const payload = { objects: res.objects[collectionName], propName: propName || collectionName };
            doDispatch(dispatch, { type: 'SET_DATA', payload: res.objects });
            doDispatch(dispatch, { type: 'OBJECTS_GOT', payload });
          } else {
            doDispatch(dispatch, { type: 'SET_DATA', payload: { loading: false } });
            return res.objects;
          }
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    initFormState(dispatch) {
      doDispatch(dispatch, { type: 'INIT_FORM' });
    },
    formInitialized(dispatch, getState, payload) {
      doDispatch(dispatch, { type: 'FORM_INITIALIZED', payload });
    },
    isSubmittingForm(dispatch) {
      doDispatch(dispatch, { type: 'SUBMIT_FORM' });
    },
    submitFormDone(dispatch, getState, payload) {
      doDispatch(dispatch, { type: 'SUBMIT_FORM_SUCCESS', payload });
    },
    initCreateForm(dispatch) {
      doDispatch(dispatch, { type: 'INIT_FORM' });
      return httpRequest('GET', url + '/new')
        .then(res => {
          doDispatch(dispatch, { type: 'FORM_INITIALIZED', payload: res });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    submitCreateForm(dispatch, getState, objectData) {
      doDispatch(dispatch, { type: 'SUBMIT_FORM' });
      const { relations, ...attributes } = objectData;
      const data = { [collectionName]: [{ ...attributes, id: 0 }], ...relations };
      return httpRequest('POST', url, data)
        .then(res => {
          const payload = { savedObject: res, successMessage: successMessageCreate };
          doDispatch(dispatch, { type: 'SUBMIT_FORM_SUCCESS', payload });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    initEditForm(dispatch, getState, id) {
      doDispatch(dispatch, { type: 'INIT_FORM' });
      return httpRequest('GET', url + `/${id}/edit`)
        .then(res => {
          doDispatch(dispatch, { type: 'FORM_INITIALIZED', payload: res });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    submitEditForm(dispatch, getState, { id, objectData }) {
      doDispatch(dispatch, { type: 'SUBMIT_FORM' });
      const { relations, ...attributes } = objectData;
      const data = { [collectionName]: [{ ...attributes }], ...relations };
      return httpRequest('PUT', url + `/${id}`, data)
        .then(res => {
          const payload = { savedObject: res, successMessage: successMessageUpdate };
          doDispatch(dispatch, { type: 'SUBMIT_FORM_SUCCESS', payload });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    updateEditedObject(dispatch, getState, field) {
      doDispatch(dispatch, { type: 'UPDATE_OBJECT_DATA', payload: field });
    },
    setData(dispatch, getState, data) {
      doDispatch(dispatch, { type: 'SET_DATA', payload: data });
    },
    getData(dispatch, getState, opts) {
      doDispatch(dispatch, { type: 'GET_DATA' });
      return httpRequest('GET', url + opts.url, null, null, opts.appConfig)
        .then(res => {
          doDispatch(dispatch, { type: 'GET_DATA_SUCCESS' });
          if (!opts.propName) return res;
          const payload = { objects: res, propName: opts.propName };
          doDispatch(dispatch, { type: 'OBJECTS_GOT', payload });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    postData(dispatch, getState, opts) {
      doDispatch(dispatch, { type: 'POST_DATA' });
      return httpRequest('POST', url + opts.url, opts.data, null, opts.appConfig)
        .then(res => {
          doDispatch(dispatch, { type: 'POST_DATA_SUCCESS' });
          if (!opts.propName) return res;
          const payload = { [opts.propName]: res };
          doDispatch(dispatch, { type: 'SET_DATA', payload });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    leaveForm(dispatch) {
      doDispatch(dispatch, { type: 'LEAVE_FORM' });
    },
    getObject(dispatch, getState, id) {
      doDispatch(dispatch, { type: 'GET_OBJECT' });
      return httpRequest('GET', url + `/${id}`)
        .then(res => {
          doDispatch(dispatch, { type: 'OBJECT_GOT', payload: res });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    deleteObject(dispatch, getState, object) {
      const objectId = object && typeof object === 'object' ? object.id : object;
      doDispatch(dispatch, { type: 'DELETE_OBJECT' });
      return httpRequest('DELETE', url + `/${objectId}`)
        .then(res => {
          doDispatch(dispatch, { type: 'DELETE_OBJECT_SUCCESS', payload: successMessageDelete });
        })
        .catch(err => {
          doDispatch(dispatch, { type: 'ERROR', payload: err });
        });
    },
    isDeleting(dispatch, getState, params) {
      doDispatch(dispatch, { type: 'DELETE_OBJECT' });
    },
    deleteDone(dispatch, getState, successMessage) {
      doDispatch(dispatch, { type: 'DELETE_OBJECT_SUCCESS', payload: successMessage });
    },
    dismissAlert(dispatch) {
      doDispatch(dispatch, { type: 'DISMISS_ALERT' });
    },
    setError(dispatch, getState, error) {
      doDispatch(dispatch, { type: 'ERROR', payload: error });
    },
  };
}

function queryFromAdvancedFilters(filters) {
  return Object.keys(filters)
    .map(filterName => {
      const filter = filters[filterName];
      const value = filter.value;
      if (!value) return null;
      let where;
      switch (filter.type) {
      case 'number':
        if (!value.min && !value.max) return null;
        where = [];
        if (value.min) where.push({ [filterName]: { gte: value.min } });
        if (value.max) where.push({ [filterName]: { lte: value.max } });
        return { $all: where };
      case 'date':
        if (!value.from && !value.to) return null;
        where = [];
        if (value.from) where.push({ [filterName]: { gte: value.from } });
        if (value.to) where.push({ [filterName]: { lte: value.to } });
        return { $all: where };
      case 'text':
        if (!value.text) return null;
        const cond = value.cond || 'match';
        return { [filterName]: { [cond]: value.text } };
      case 'boolean':
        if (!value.set) return null;
        const bool = value.set === 'set';
        return { [filterName]: bool };
      case 'relation':
        return { [filterName]: value };
      default:
        return null;
      }
    })
    .filter(f => !!f);
}



// WEBPACK FOOTER //
// ../hyperdb-utils/src/triggers.js